import * as Highcharts from "highcharts"
import "highcharts/highcharts-more"
import { applyHighchartsTheme } from "../utils/highcharts_theme"

// Get locale from server-provided I18n config
function getLocale() {
  return window.I18n?.numberFormat?.locale || "en"
}

// Calculate min/max bounds with padding for data labels
function determineMinMax(...series) {
  const allValues = series
    .reduce((all, s) => all.concat(s ?? []), [])
    .map((point) => point.y)

  if (allValues.length === 0) return { min: 0, max: 100 }

  let minValue = Math.min(...allValues)
  let maxValue = Math.max(...allValues)
  const dataRange = maxValue - minValue

  const centerValue = (minValue + maxValue) / 2
  const minRange = Math.max(centerValue * 0.2, 10)

  // If the data range is too small, expand the axis limits
  if (dataRange < minRange) {
    const expansion = (minRange - dataRange) / 2
    minValue = minValue - expansion
    maxValue = maxValue + expansion
  }

  // Add 75% on each side to give space for the labels
  const padding = (maxValue - minValue) * 0.75
  return { min: minValue - padding, max: maxValue + padding }
}

export function renderProposalProgressChart(
  el,
  chartData,
  callbacks = {},
  options = {},
) {
  const isClientPortal = chartData.is_client_portal === true
  const currencySymbol = options.currencySymbol || chartData.currency_symbol
  const areaUnit = options.areaUnit || chartData.area_unit
  const metricLabel = options.metricLabel || chartData.metric_label
  const labelClasses =
    options.rootScale === "report"
      ? {
          baseWithLeading: "text-base/5!", // root-scale: report
          xs: "text-xs", // root-scale: report
          xsImportant: "text-xs!", // root-scale: report
          xsWithoutLeading: "text-xs/none!", // root-scale: report
        }
      : {
          baseWithLeading: "text-legacy-base! leading-[12.5px]!",
          xs: "text-legacy-xs",
          xsImportant: "text-legacy-xs!",
          xsWithoutLeading: "text-legacy-xs/none!",
        }

  if (!currencySymbol) {
    throw new Error(
      "Currency symbol is required. Currency symbols must be passed from backend based on deal/account currency.",
    )
  }
  if (!areaUnit) {
    throw new Error(
      "Area unit is required. Area units must be passed from backend based on deal/account unit.",
    )
  }

  function formatNumber(value) {
    return new Intl.NumberFormat(getLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  // Format price per unit with data-driven currency symbol
  function formatPricePerUnit(value) {
    return `${currencySymbol}${formatNumber(value)}/${areaUnit}`
  }

  // Format price showing both annual and monthly values
  const yearSuffix = chartData.year_suffix
  const monthSuffix = chartData.month_suffix || "/M"

  function formatPriceAnnualAndMonthly(value) {
    const monthly = value / 12
    return `${currencySymbol}${formatNumber(value)}/${areaUnit}${yearSuffix} (${currencySymbol}${formatNumber(monthly)}/${areaUnit}${monthSuffix})`
  }

  // Use backend-calculated values if available, otherwise calculate locally
  // Client Portal passes global_min/max from backend with 15% padding
  // Occupancy Cost Report doesn't pass these, so we calculate with 75% padding for labels
  const calculatedLimits = determineMinMax(
    chartData.occupier_series,
    chartData.landlord_series,
  )
  const axisLimits = {
    min: chartData.global_min ?? calculatedLimits.min,
    max: chartData.global_max ?? calculatedLimits.max,
  }

  function dataPointLabel({ type, color, name, value }) {
    if (isClientPortal) {
      return `<div style="display: flex; flex-direction: column; text-align: left;">
        <span style="text-transform: uppercase; color: ${color}; font-size: 10px; line-height: 1; font-weight: 600;">${type}</span>
        <span style="color: #000; font-size: 12px; line-height: 1.25; font-weight: 500;">${name}</span>
        <span style="text-transform: uppercase; color: #666; font-size: 10px; font-weight: 400;">${value}</span>
      </div>`
    } else {
      return `<div class="flex flex-col text-left">
        <span class="uppercase ${color} ${labelClasses.xsImportant} leading-none!">${type}</span>
        <span class="text-black ${labelClasses.baseWithLeading} font-normal!">${name}</span>
        <span class="uppercase text-gray-600 ${labelClasses.xs} font-normal!">${value}</span>
      </div>`
    }
  }

  const series = []

  if (chartData.landlord_series && chartData.landlord_series.length > 0) {
    const landlordSeries = {
      name: "Landlord",
      data: chartData.landlord_series.map((point) => ({
        x: point.x - 1,
        y: point.y,
        proposal_id: point.id,
      })),
      color: chartData.landlord_color,
      marker: { enabled: true, radius: 4, symbol: "circle" },
      dataLabels: {
        enabled: true,
        verticalAlign: "bottom",
        y: -10,
        formatter: function () {
          const proposal = chartData.landlord_series[this.point.index]
          return dataPointLabel({
            color: isClientPortal
              ? chartData.landlord_color
              : `text-[${chartData.landlord_color}]`,
            type: "Landlord",
            name: proposal.name,
            value:
              proposal.formatted_value || formatPriceAnnualAndMonthly(this.y),
          })
        },
        useHTML: true,
      },
    }

    if (callbacks.onPointClick) {
      landlordSeries.cursor = "pointer"
      landlordSeries.point = {
        events: {
          click: (e) => callbacks.onPointClick(e.point.options.proposal_id),
        },
      }
    }

    series.push(landlordSeries)
  }

  if (chartData.occupier_series && chartData.occupier_series.length > 0) {
    const occupierSeries = {
      name: "Occupier",
      color: chartData.occupier_color,
      data: chartData.occupier_series.map((point) => ({
        x: point.x - 1,
        y: point.y,
        proposal_id: point.id,
      })),
      marker: { enabled: true, radius: 4, symbol: "circle" },
      dataLabels: {
        enabled: true,
        verticalAlign: "top",
        y: 10,
        formatter: function () {
          const proposal = chartData.occupier_series[this.point.index]
          return dataPointLabel({
            color: isClientPortal
              ? chartData.occupier_color
              : `text-[${chartData.occupier_color}]`,
            type: "Occupier",
            name: proposal.name,
            value:
              proposal.formatted_value || formatPriceAnnualAndMonthly(this.y),
          })
        },
        useHTML: true,
      },
    }

    if (callbacks.onPointClick) {
      occupierSeries.cursor = "pointer"
      occupierSeries.point = {
        events: {
          click: (e) => callbacks.onPointClick(e.point.options.proposal_id),
        },
      }
    }

    series.push(occupierSeries)
  }

  if (chartData.current_delta) {
    const deltas = [
      [
        chartData.current_delta.x,
        chartData.current_delta.min,
        chartData.current_delta.max,
      ],
    ]
    const deltaLabel = chartData.delta_label || `${metricLabel} delta`
    series.push({
      name: deltaLabel,
      type: "columnrange",
      data: deltas,
      color: "rgba(0, 0, 0, 0.05)",
      maxPointWidth: 110,
      pointPadding: 0,
      groupPadding: 0,
      borderRadius: 0,
      dataLabels: [
        {
          enabled: true,
          align: "left",
          verticalAlign: "bottom",
          y: -5,
          formatter: function () {
            if (this.y !== null && this.point.high === this.y) {
              const delta = this.point.high - this.point.low
              const formattedDelta =
                chartData.current_delta.formatted_delta ||
                formatPricePerUnit(delta)
              if (isClientPortal) {
                return `<div style="text-transform: uppercase; line-height: 1;">
                <span style="font-size: 10px; font-weight: 700; color: #dc2626; white-space: nowrap;">${deltaLabel}</span><br>
                <span style="font-size: 14px; line-height: 1.25; white-space: nowrap;">${formattedDelta}</span>
              </div>`
              } else {
                return `<div class="uppercase! leading-none!">
                <span class="${labelClasses.xsWithoutLeading} font-bold! text-red-600 whitespace-nowrap!">${deltaLabel}</span><br>
                <span class="${labelClasses.baseWithLeading} whitespace-nowrap!">${formattedDelta}</span>
              </div>`
              }
            }
            return null
          },
          useHTML: true,
          style: {
            color: "#000000",
            textOutline: "none",
            fontWeight: "normal",
            textAlign: "left",
          },
        },
      ],
    })
  }

  function drawLines(chart) {
    if (chart.customLines) {
      chart.customLines.forEach((line) => line.destroy())
    }
    chart.customLines = []

    if (chartData.current_delta) {
      const columnSeries = chart.series.find((s) => s.type === "columnrange")
      if (columnSeries && columnSeries.points.length > 0) {
        const {
          x,
          shapeArgs: { width: deltaWidth },
        } = columnSeries.points[0]
        const minX = chart.xAxis[0].toPixels(x) - deltaWidth * 1.5
        const maxX = chart.xAxis[0].toPixels(x) + deltaWidth / 2
        const minY = chart.yAxis[0].toPixels(chartData.current_delta.min)
        const maxY = chart.yAxis[0].toPixels(chartData.current_delta.max)
        const style = {
          "stroke-width": 1,
          stroke: "rgba(0, 0, 0, 0.4)",
          zIndex: 10,
        }

        const bottomLine = chart.renderer
          .path(["M", minX, minY, "L", maxX, minY])
          .attr(style)
          .add()
        const topLine = chart.renderer
          .path(["M", minX, maxY, "L", maxX, maxY])
          .attr(style)
          .add()
        chart.customLines = [bottomLine, topLine]
      }
    }
  }

  applyHighchartsTheme(Highcharts)

  return Highcharts.chart(el, {
    chart: {
      // Highcharts 13 defaults chart.backgroundColor to a CSS var that renders
      // dark under prefers-color-scheme: dark; keep it explicit.
      backgroundColor: "transparent",
      spacingTop: 12,
      spacingBottom: 22,
      spacingLeft: 12,
      spacingRight: 12,
      events: {
        load: function () {
          drawLines(this)
          if (callbacks.onLoad) callbacks.onLoad(this)
        },
        redraw: function () {
          drawLines(this)
          if (callbacks.onRedraw) callbacks.onRedraw(this)
        },
      },
    },
    title: isClientPortal
      ? { text: null }
      : {
          text: chartData.property_name,
          align: "left",
          style: { fontSize: "16px", fontWeight: "bold" },
          margin: 12,
        },
    subtitle: isClientPortal
      ? { text: null }
      : {
          text: chartData.property_unit,
          align: "left",
          style: { color: "#666", fontSize: "12px" },
          y: 25,
        },
    tooltip: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      type: "category",
      categories: new Array(chartData.total_proposals).fill(),
      labels: { enabled: false },
      lineWidth: 0,
      tickLength: 0,
      minorTickLength: 0,
    },
    yAxis: {
      title: {
        text:
          chartData.y_axis_title ||
          `${metricLabel} (${currencySymbol}/${areaUnit})`,
        align: "low",
        rotation: 0,
        y: 8,
      },
      startOnTick: false,
      endOnTick: false,
      min: axisLimits.min,
      max: axisLimits.max,
      labels: {
        formatter: function () {
          const value = Highcharts.numberFormat(this.pos, 0)
          return this.isLast ? `${value}` : value
        },
        color: "#666",
      },
    },
    plotOptions: {
      series: { marker: { enabled: true } },
      columnrange: { stacking: undefined },
    },
    series: series,
    credits: { enabled: false },
    exporting: { enabled: false },
  })
}

export function initProposalProgressCharts() {
  document
    .querySelectorAll('[data-chart="proposal-progress"]:not([phx-hook])')
    .forEach((el) => {
      const options = {
        currencySymbol: el.dataset.currencySymbol,
        areaUnit: el.dataset.areaUnit,
        rootScale: el.dataset.rootScale || "main",
      }
      renderProposalProgressChart(
        el,
        JSON.parse(el.dataset.chartData),
        {},
        options,
      )
    })
}
